/**
 * Utility functions for iframe full screen mode positioning
 */

export interface FullScreenPositionOptions {
  container: HTMLElement
  canvasContentSelector?: string
}

/**
 * Positions an iframe container in full screen mode
 * Calculates height from current position to bottom of parent window
 */
export function positionFullScreenIframe(options: FullScreenPositionOptions): void {
  const { container, canvasContentSelector = '.canvas-builder-layout__content' } = options

  const canvasContent = document.querySelector(canvasContentSelector)
  if (!canvasContent) return

  const rect = canvasContent.getBoundingClientRect()

  // Calculate available height from canvas content position to bottom of window
  let availableHeight: number

  try {
    // Check if we're in an iframe and can access parent window
    const iframeRect = window.frameElement?.getBoundingClientRect()

    if (iframeRect && window.parent !== window) {
      // We're in an iframe - try to access parent window height
      try {
        const parentHeight = window.parent.innerHeight
        const absoluteTop = iframeRect.top + rect.top
        availableHeight = parentHeight - absoluteTop
      } catch (crossOriginError) {
        // Cross-origin access denied - fall back to current window height
        availableHeight = window.innerHeight - rect.top
      }
    } else {
      // Not in iframe or same-origin - use current window
      availableHeight = window.innerHeight - rect.top
    }
  } catch (error) {
    // Fallback to current window height calculation
    availableHeight = window.innerHeight - rect.top
  }

  const finalHeight = Math.max(availableHeight, 0)

  // Update container style to use calculated height
  Object.assign(container.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${finalHeight}px`,
    zIndex: '10',
    margin: '0',
    padding: '0',
    transform: 'none',
  })
}

/**
 * Sets up full screen mode for an iframe component
 */
export function setupFullScreenMode(
  container: HTMLElement,
  isFullScreenMode: () => boolean,
  options?: Partial<FullScreenPositionOptions>,
): () => void {
  const positionFunction = () => {
    if (isFullScreenMode() && container) {
      positionFullScreenIframe({ container, ...options })
    }
  }

  // Execute immediately if already in full screen mode
  positionFunction()

  // Add event listeners
  window.addEventListener('resize', positionFunction)
  window.addEventListener('scroll', positionFunction, true)
  const interval = setInterval(positionFunction, 250)

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', positionFunction)
    window.removeEventListener('scroll', positionFunction, true)
    clearInterval(interval)
  }
}
